import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"
import 'tom-select/dist/css/tom-select.css'
import '../stylesheets/tom-select.scss'

export default class extends Controller {
  static targets = ["select"]
  static values = {
    url: String
  }

  connect() {
    this.initializeTomSelect()
  }

  initializeTomSelect() {
    if (!this.hasSelectTarget) return;

    this.tomselect = new TomSelect(this.selectTarget, {
      valueField: 'id',
      labelField: 'text',
      searchField: ['text'],
      placeholder: "Kompetenz suchen oder neue erstellen...",
      openOnFocus: true,
      preload: 'focus',
      closeAfterSelect: true,
      selectOnTab: true,
      maxOptions: 100,
      create: true,
      createOnBlur: false,
      createFilter: (input) => {
        return input.length >= 2; // Mindestens 2 Zeichen für neue Kompetenz
      },
      create: (input) => {
        return {
          id: 'new_' + input,
          text: `"${input}" erstellen`,
          isNew: true
        };
      },
      load: (query, callback) => {
        if (!query.length) return callback();
        
        // Verwende die Backend-Such-API
        const url = `/de/backend/search/?model=competencies&term=${encodeURIComponent(query)}`
        
        fetch(url)
          .then(response => response.json())
          .then(json => {
            // Prüfe auf ähnliche Kompetenzen für Warnung
            const similarCompetencies = json.filter(item => 
              item.text.toLowerCase().includes(query.toLowerCase()) ||
              query.toLowerCase().includes(item.text.toLowerCase())
            );
            
            if (similarCompetencies.length > 0 && query.length >= 3) {
              // Füge Warnung hinzu
              json.unshift({
                id: 'warning',
                text: `⚠️ Ähnliche Kompetenzen gefunden: ${similarCompetencies.map(c => c.text).join(', ')}`,
                disabled: true
              });
            }
            
            callback(json)
          }).catch(() => {
            callback()
          })
      },
      render: {
        no_results: function(data, escape) {
          return '<div class="no-results">Keine Ergebnisse für "' + escape(data.input) + '"</div>';
        },
        option_create: function(data, escape) {
          return '<div class="create">Hinzuf&uuml;gen: <strong>' + escape(data.input) + '</strong>&hellip;</div>';
        },
        option: function(item, escape) {
          if (item.isNew) {
            return `<div class="option text-green-600">
              <span class="title text-sm font-medium">${escape(item.text)}</span>
              <span class="subtext text-xs text-green-500">Neue Kompetenz erstellen</span>
            </div>`
          } else if (item.id === 'warning') {
            return `<div class="option text-yellow-600 bg-yellow-50">
              <span class="title text-sm">${escape(item.text)}</span>
            </div>`
          } else {
            return `<div class="option">
              <span class="title text-sm">${escape(item.text)}</span>
              ${item.subtext ? `<span class="subtext text-xs text-gray-500">${item.subtext}</span>` : ''}
            </div>`
          }
        },
        item: function(item, escape) {
          if (item.isNew) {
            return `<div class="item text-sm text-green-600">${escape(item.text.replace(' erstellen', ''))}</div>`
          } else {
            return `<div class="item text-sm">${escape(item.text)}</div>`
          }
        }
      },
      onChange: (value) => {
        if (value) {
          this.addCompetency(value);
        }
      }
    })
  }

  setWeight(event) {
    const weight = event.currentTarget.dataset.weight
    const hiddenField = this.selectTarget.closest('form').querySelector('input[name="competency_requirement[weight]"]')
    if (hiddenField) {
      hiddenField.value = weight
    }
    
    // Aktualisiere die visuelle Darstellung
    const buttons = this.element.querySelectorAll('[data-action*="setWeight"]');
    buttons.forEach(button => {
      if (button.dataset.weight === weight) {
        button.classList.add('bg-atorange-50', 'border-atorange-500', 'text-atorange-700');
        button.classList.remove('bg-white', 'text-gray-700');
      } else {
        button.classList.remove('bg-atorange-50', 'border-atorange-500', 'text-atorange-700');
        button.classList.add('bg-white', 'text-gray-700');
      }
    });
  }

  addCompetency(competencyId) {
    const urlParts = window.location.pathname.split('/');
    const requisitionIndex = urlParts.indexOf('requisitions');
    if (requisitionIndex === -1) {
      return;
    }

    const requisitionId = urlParts[requisitionIndex + 1];
    
    // Prüfe ob es eine neue Kompetenz ist
    if (competencyId.startsWith('new_')) {
      const newCompetencyTitle = competencyId.replace('new_', '');
      
      // Erstelle zuerst die Kompetenz
      this.createCompetency(newCompetencyTitle, requisitionId);
    } else {
      // Verwende bestehende Kompetenz
      const url = `/de/backend/requisitions/${requisitionId}/competency_requirements`;

      const formData = new FormData();
      formData.append('competency_requirement[competency_id]', competencyId);

      this.sendRequest(url, formData);
    }
  }

  createCompetency(title, requisitionId) {
    const url = '/de/backend/competencies';
    const formData = new FormData();
    formData.append('competency[title]', title);
    // Für Skills: immer Tenant-Katalog verwenden
    formData.append('competency_type', 'skill');
    
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Jetzt füge die Kompetenz zur Requisition hinzu
        this.addCompetencyToRequisition(data.competency_id, requisitionId);
      } else {
        this.showError('Fehler beim Erstellen der Kompetenz: ' + (data.errors || 'Unbekannter Fehler'));
      }
    })
    .catch(error => {
      this.showError('Netzwerkfehler beim Erstellen der Kompetenz');
    });
  }

  addCompetencyToRequisition(competencyId, requisitionId) {
    const url = `/de/backend/requisitions/${requisitionId}/competency_requirements`;
    const formData = new FormData();
    formData.append('competency_requirement[competency_id]', competencyId);

    this.sendRequest(url, formData);
  }

  sendRequest(url, formData) {
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => {
      if (response.ok) {
        this.tomselect.clear();
        this.tomselect.clearOptions();
        
        // Verarbeite Turbo Stream Response
        return response.text();
      } else {
        this.showError('Fehler beim Hinzufügen der Kompetenz');
        throw new Error('Request failed');
      }
    })
    .then(html => {
      if (html) {
        // Verarbeite die Turbo Stream Response
        Turbo.renderStreamMessage(html);
      }
    })
    .catch(error => {
      this.showError('Netzwerkfehler beim Hinzufügen der Kompetenz');
    });
  }

  getSkillCategoryId() {
    // Versuche die Skill-Kategorie-ID zu finden oder verwende einen Standard
    // Dies könnte auch über eine API-Abfrage erfolgen
    return 1; // Standard Skill-Kategorie ID
  }

  showError(message) {
    // Erstelle eine temporäre Fehlermeldung
    const errorDiv = document.createElement('div');
    errorDiv.className = 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4';
    errorDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm">${message}</p>
        </div>
      </div>
    `;
    
    // Füge die Fehlermeldung vor dem Select hinzu
    this.selectTarget.parentNode.insertBefore(errorDiv, this.selectTarget);
    
    // Entferne die Fehlermeldung nach 5 Sekunden
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv);
      }
    }, 5000);
  }

  disconnect() {
    if (this.tomselect) {
      this.tomselect.destroy();
    }
  }
} 